import { BrandFilterKey, BrandStatus, BrandVisibilityControl } from '../../../../models/enum/shared/brand.enum';
import { Brand } from '../../../../models/product/dto/brand';
import { exists } from '../../../../functions/exists';

export interface BrandFilterState {
  readonly selectedFilters: ReadonlyMap<BrandFilterKey, readonly string[]>;
  readonly visibilityControls: ReadonlySet<BrandVisibilityControl>;
  readonly searchText: string;
}

interface BrandFilterIndexEntry {
  readonly normalizedBrandName: string;
  readonly filterValuesByKey: ReadonlyMap<BrandFilterKey, ReadonlySet<string>>;
  readonly visibilityControls: ReadonlySet<BrandVisibilityControl>;
}

export class BrandFilterIndex {
  private orderedIds: string[] = [];
  private brandById = new Map<string, Brand>();
  private entryById = new Map<string, BrandFilterIndexEntry>();
  private idsByFilterKeyAndValue = new Map<BrandFilterKey, Map<string, Set<string>>>();
  private brandIdsByVisibilityControl = new Map<BrandVisibilityControl, Set<string>>();

  reconcile(brands: Brand[] | null): void {
    this.clear();
    if (!brands?.length) return;

    const seenIds = new Set<string>();
    const uniqueBrands: Brand[] = [];
    for (let index = brands.length - 1; index >= 0; index--) {
      const brand = brands[index];
      if (!brand?.id || seenIds.has(brand.id)) continue;
      seenIds.add(brand.id);
      uniqueBrands.unshift(brand);
    }

    uniqueBrands.forEach(brand => this.insertBrand(brand));
  }

  clear(): void {
    this.orderedIds = [];
    this.brandById = new Map<string, Brand>();
    this.entryById = new Map<string, BrandFilterIndexEntry>();
    this.idsByFilterKeyAndValue = new Map<BrandFilterKey, Map<string, Set<string>>>();
    this.brandIdsByVisibilityControl = new Map<BrandVisibilityControl, Set<string>>();
  }

  apply(state: BrandFilterState): Brand[] {
    const activeMatches = [
      this.findIdsMatchingFilters(state.selectedFilters),
      this.findIdsMatchingVisibility(state.visibilityControls),
      this.findIdsMatchingSearch(state.searchText)
    ].filter((matchingIds): matchingIds is Set<string> => matchingIds !== null);

    const matchingIds =
      activeMatches.length === 0 ? new Set<string>(this.orderedIds) : this.intersectIdSets(activeMatches);
    return this.mapIdsToBrands(matchingIds);
  }

  private insertBrand(brand: Brand): void {
    const id = brand.id;
    if (!this.brandById.has(id)) {
      this.orderedIds.push(id);
    }

    const entry = this.buildEntry(brand);
    this.brandById.set(id, brand);
    this.entryById.set(id, entry);
    this.addToLookupIndexes(id, entry);
  }

  private buildEntry(brand: Brand): BrandFilterIndexEntry {
    const filterValuesByKey = new Map<BrandFilterKey, ReadonlySet<string>>();
    filterValuesByKey.set(BrandFilterKey.State, this.collectValues([brand.stateCode]));
    filterValuesByKey.set(BrandFilterKey.City, this.collectValues([brand.city]));
    filterValuesByKey.set(BrandFilterKey.Availability, this.collectValues([brand.getAvailabilityFilterValue()]));
    filterValuesByKey.set(BrandFilterKey.Trend, this.collectValues([brand.availabilityScore?.trendDirection]));
    filterValuesByKey.set(BrandFilterKey.Completeness, this.collectValues([brand.getCompletenessFilterValue()]));

    return {
      normalizedBrandName: this.normalizeSearchText(brand.name),
      filterValuesByKey,
      visibilityControls: this.buildVisibilityControls(brand)
    };
  }

  private collectValues(values: readonly (string | null | undefined)[] | null | undefined): Set<string> {
    return new Set(values?.filter(value => value !== null && value !== undefined && value.length > 0) ?? []);
  }

  private buildVisibilityControls(brand: Brand): Set<BrandVisibilityControl> {
    const controls = new Set<BrandVisibilityControl>();
    const statuses = brand.statuses?.filter(exists) ?? [];
    if (!statuses.length) {
      controls.add(BrandVisibilityControl.Complete);
      return controls;
    }
    statuses.forEach(status => {
      const control = BrandFilterIndex.visibilityControlForStatus(status);
      if (control) controls.add(control);
    });
    return controls;
  }

  private addToLookupIndexes(id: string, entry: BrandFilterIndexEntry): void {
    entry.filterValuesByKey.forEach((values, filterKey) => {
      let idsByValue = this.idsByFilterKeyAndValue.get(filterKey);
      if (!idsByValue) {
        idsByValue = new Map<string, Set<string>>();
        this.idsByFilterKeyAndValue.set(filterKey, idsByValue);
      }
      values.forEach(value => {
        let matchingIds = idsByValue.get(value);
        if (!matchingIds) {
          matchingIds = new Set<string>();
          idsByValue.set(value, matchingIds);
        }
        matchingIds.add(id);
      });
    });

    entry.visibilityControls.forEach(control => {
      let matchingIds = this.brandIdsByVisibilityControl.get(control);
      if (!matchingIds) {
        matchingIds = new Set<string>();
        this.brandIdsByVisibilityControl.set(control, matchingIds);
      }
      matchingIds.add(id);
    });
  }

  private findIdsMatchingFilters(selectedFilters: ReadonlyMap<BrandFilterKey, readonly string[]>): Set<string> | null {
    const matchesByFilter: Set<string>[] = [];
    selectedFilters?.forEach((selectedValues, filterKey) => {
      if (selectedValues.length === 0) return;
      matchesByFilter.push(this.lookupIdsForValues(filterKey, selectedValues));
    });
    return matchesByFilter.length === 0 ? null : this.intersectIdSets(matchesByFilter);
  }

  private lookupIdsForValues(filterKey: BrandFilterKey, selectedValues: readonly string[]): Set<string> {
    const matchingIds = new Set<string>();
    const idsByValue = this.idsByFilterKeyAndValue.get(filterKey);
    if (!idsByValue) return matchingIds;

    selectedValues.forEach(selectedValue => {
      idsByValue.get(selectedValue)?.forEach(id => matchingIds.add(id));
    });
    return matchingIds;
  }

  private findIdsMatchingVisibility(disabledControls: ReadonlySet<BrandVisibilityControl>): Set<string> | null {
    if (!disabledControls?.size) return null;

    const matchingIds = new Set<string>();
    this.brandIdsByVisibilityControl.forEach((idsForControl, control) => {
      if (disabledControls.has(control)) return;
      idsForControl.forEach(id => matchingIds.add(id));
    });
    return matchingIds;
  }

  private findIdsMatchingSearch(searchText: string): Set<string> | null {
    const normalizedSearchText = this.normalizeSearchText(searchText);
    if (!normalizedSearchText) return null;

    const matchingIds = new Set<string>();
    this.entryById.forEach((entry, id) => {
      if (entry.normalizedBrandName.includes(normalizedSearchText)) {
        matchingIds.add(id);
      }
    });
    return matchingIds;
  }

  private normalizeSearchText(value: string | null | undefined): string {
    return (value ?? '').toLowerCase().trim();
  }

  private intersectIdSets(sets: readonly ReadonlySet<string>[]): Set<string> {
    if (sets.length === 0) return new Set<string>();
    let result = new Set<string>(sets[0]);
    for (let index = 1; index < sets.length; index++) {
      const intersection = new Set<string>();
      for (const id of result) {
        if (sets[index].has(id)) {
          intersection.add(id);
        }
      }
      result = intersection;
    }
    return result;
  }

  private mapIdsToBrands(matchingIds: ReadonlySet<string>): Brand[] {
    const brands: Brand[] = [];
    for (const id of this.orderedIds) {
      const brand = this.brandById.get(id);
      if (brand && matchingIds.has(id)) {
        brands.push(brand);
      }
    }
    return brands;
  }

  private static visibilityControlForStatus(status: BrandStatus): BrandVisibilityControl | null {
    switch (status) {
      case BrandStatus.MissingLogo:
        return BrandVisibilityControl.MissingLogo;
      case BrandStatus.MissingProductGroups:
        return BrandVisibilityControl.NoProductGroup;
      case BrandStatus.MissingProfile:
        return BrandVisibilityControl.IncompleteProfile;
      case BrandStatus.MissingProductMap:
        return BrandVisibilityControl.MissingProductMap;
      default:
        return null;
    }
  }
}
